import type {
  WithContext,
  Product,
  Car,
  PropertyValue,
  LocalBusiness,
  EngineSpecification,
} from "schema-dts";

import { TVehicle } from "types";

export const getJsonLd = (
  vehicle: TVehicle,
  translate: any = ({ defaultMessage }: TMessageId) => defaultMessage,
) => {
  const product: Product = getProduct(vehicle, translate);
  const car: Car = getCar(vehicle, translate);
  const json = {
    ...product,
    ...car,
    "@context": "https://schema.org",
    "@type": ["Product", "Car"],
  };

  return json;
};

type TMessageId = { id: string; defaultMessage: string };

const getProduct = (
  vehicle: TVehicle,
  translate: any = ({ defaultMessage }: TMessageId) => defaultMessage,
  url: string = "https://whatever.carbando.eu/fahrzeuge/#catalog/dt/",
): WithContext<Product> => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: `${vehicle.modell?.marke?.name || ""} ${vehicle.modell?.name || ""} ${vehicle.version || ""}`.trim(),
  description:
    `${vehicle.modell?.marke?.name || ""} ${vehicle.modell?.name || ""} ${vehicle.version || ""}`.trim(),
  url: url + (vehicle.wgnr || ""),
  sku: vehicle.wgnr || "",
  image: getImage(vehicle),
  brand: {
    "@type": "Brand",
    name: vehicle.modell?.marke?.name || "",
  },
  model: vehicle.modell?.name || "",

  color: translate({
    id: vehicle.farbe_aussen?.id
      ? `farbe.${vehicle.farbe_aussen.id}.label`
      : "farbe.unknown.label",
    defaultMessage: vehicle.farbe_aussen?.name || "",
  }),
  offers: [
    {
      "@type": "Offer",
      price: vehicle.preis ?? "",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      availableAtOrFrom: getLocations(vehicle, translate),
      itemCondition:
        vehicle.fzart?.name === "Neuwagen" ? "NewCondition" : "UsedCondition",
    },
  ],
});

const getCar = (
  vehicle: TVehicle,
  translate: any = ({ defaultMessage }: TMessageId) => defaultMessage,
): WithContext<Car> => ({
  "@context": "https://schema.org",
  "@type": "Car",
  bodyType: translate({
    id: vehicle.aufbau?.id
      ? `aufbau.${vehicle.aufbau.id}.label`
      : "aufbau.unknown.label",
    defaultMessage: vehicle.aufbau?.name || "",
  }),
  vehicleEngine: getEngine(vehicle),
  dateVehicleFirstRegistered: vehicle.erstzulassung || undefined,
  emissionsCO2:
    vehicle.wltp_co2_emissionen ||
    vehicle.wltp_co2_emissionen_entladen ||
    undefined,
  fuelConsumption: {
    "@type": "QuantitativeValue",
    unitText: "l/100km",
    value:
      vehicle.wltp_kombiniert || vehicle.wltp_kombiniert_entladen || undefined,
  },
  mileageFromOdometer: {
    "@type": "QuantitativeValue",
    value: vehicle.km || undefined,
    unitCode: "KMT",
  },
  vehicleInteriorColor: vehicle.farbe_innen?.name || undefined,
  vehicleSeatingCapacity: vehicle.sitze || undefined,
  numberOfDoors: vehicle.tueren || undefined,
  numberOfForwardGears: vehicle.gaenge || undefined,
  numberOfPreviousOwners: vehicle.vorbesitzer || undefined,
  vehicleTransmission: vehicle.getriebe
    ? translate({
        id: vehicle.getriebe?.id
          ? `getriebe.${vehicle.getriebe.id}.label`
          : "getriebe.unknown.label",
        defaultMessage: vehicle.getriebe?.name || "",
      })
    : undefined,
  vehicleConfiguration: vehicle.version || "",
  additionalProperty: [
    ...getFeatures(vehicle, translate),
    {
      "@type": "PropertyValue",
      name: "Umweltplakette",
      value: vehicle.umweltplakette?.name || "",
    },
    {
      "@type": "PropertyValue",
      name: "Vorbesitzer",
      value: vehicle.vorbesitzer || 0,
    },
  ],
});

const getEngine = (
  vehicle: TVehicle,
  translate: any = ({ defaultMessage }: TMessageId) => defaultMessage,
): WithContext<EngineSpecification> => ({
  "@context": "https://schema.org",
  "@type": "EngineSpecification",

  fuelType: translate({
    id: vehicle.kraftstoff?.id
      ? `kraftstoff.${vehicle.kraftstoff.id}.label`
      : "kraftstoff.unknown.label",
    defaultMessage: vehicle.kraftstoff?.name || "",
  }),
  enginePower: {
    "@type": "QuantitativeValue",
    unitCode: "KWT",
    value: vehicle.kw ?? undefined,
  },
});

const getImage = (
  vehicle: TVehicle,
  translate: any = ({ defaultMessage }: TMessageId) => defaultMessage,
): string[] => {
  const pictures = vehicle.pictures;
  const picturesPath = vehicle.picturesPath || "";
  const firstPicture = vehicle.firstPicture;
  const wgnr = vehicle.wgnr || "";
  let picture: any[] = [];
  if (pictures && Array.isArray(pictures) && pictures.length > 0)
    picture = pictures.map((p) => p.src);
  else if (firstPicture) picture = [firstPicture];
  else if (picturesPath && wgnr) picture = [picturesPath + wgnr + "_1.jpg"];
  else picture = [];

  return picture;
};

const getFeatures = (
  vehicle: TVehicle,
  translate: any = ({ defaultMessage }: TMessageId) => defaultMessage,
) => {
  const ausstattung = Array.isArray(vehicle.ausstattung)
    ? vehicle.ausstattung
    : [];
  const features = ausstattung.map(
    ({ id, name }): PropertyValue => ({
      "@type": "PropertyValue",
      value: true,
      name: translate({
        id: id ? `ausstattung.${id}.label` : "ausstattung.unknown.label",
        defaultMessage: name || "",
      }),
    }),
  );
  return features;
};

const getLocations = (
  vehicle: TVehicle,
  translate: any = ({ defaultMessage }: TMessageId) => defaultMessage,
): LocalBusiness[] => {
  const locations = Array.isArray(vehicle.locations) ? vehicle.locations : [];
  const location = vehicle.location;
  const elements =
    locations.length > 0 ? locations : location ? [location] : [];
  const result = elements.map(
    (loc): LocalBusiness => ({
      "@type": "LocalBusiness",
      name: translate({
        id: loc?.id
          ? `location.${loc.id}.fullname`
          : "location.unknown.fullname",
        defaultMessage: loc?.name || "",
      }),
      address: {
        "@type": "PostalAddress",
        addressCountry: "DE",
        streetAddress: loc?.address || "",
        postalCode: loc?.zip || "",
        addressLocality: loc?.city || "",
        telephone: loc?.phone || "",
        email: loc?.email || "",
      },
    }),
  );
  return result;
};
